import { Injectable } from '@angular/core';
import { ToastService } from '@teorema/patron';
import { Ticket } from 'src/app/models';

@Injectable({ providedIn: 'root' })
export class TicketIaClipboardService {
  constructor(private toastService: ToastService) {}

  async copy(ticket: Ticket): Promise<void> {
    if (!ticket) {
      this.toastService.show({
        color: 'warn',
        message: 'Chamado não localizado para copiar as informações.',
      });
      return;
    }

    const payload = this.buildPayload(ticket);
    const text = JSON.stringify(payload, null, 2);

    const success = await this.writeToClipboard(text);
    if (success) {
      this.toastService.show({
        color: 'success',
        message: 'Informações do chamado copiadas para a área de transferência.',
      });
    } else {
      this.toastService.show({
        color: 'error',
        message: 'Não foi possível copiar as informações do chamado.',
      });
    }
  }

  private buildPayload(ticket: Ticket) {
    const safeTicket: any = ticket;

    const overview = {
      ticket_id: ticket.id ?? null,
      ticket_reference: safeTicket.ref ?? null,
      teorema_ticket_number: ticket.ticket_number_teorema ?? null,
      integrator_ticket_number: safeTicket.ticket_number ?? null,
      short_summary: safeTicket.summary ?? null,
      detailed_description: safeTicket.description ?? null,
      problem_description: safeTicket.problem ?? null,
      ticket_situation_code: ticket.situation ?? null,
      ticket_status: this.mapEntity(safeTicket.status ?? ticket.status, 'status'),
      product_system: this.mapEntity(safeTicket.system ?? ticket.system, 'system'),
      customer_account: this.mapEntity(safeTicket.customer ?? ticket.customer, 'customer'),
      customer_account_code: this.getCustomerCode(ticket),
      agent_company: safeTicket.agent ?? null,
      primary_responsible: this.mapUser(safeTicket.responsible ?? ticket.responsible),
      assigned_developer: this.mapUser(safeTicket.developer ?? ticket.developer),
      requester_contact: this.mapUser(safeTicket.requester ?? ticket.requester),
      priority_level: safeTicket.priority ?? null,
      urgency_flag:
        safeTicket.urgent === undefined || safeTicket.urgent === null
          ? null
          : Boolean(safeTicket.urgent),
      ticket_type: this.mapEntity(safeTicket.ticket_type, 'ticket_type'),
      ticket_category_code: safeTicket.type ?? null,
      release_status: safeTicket.release_situation ?? null,
      created_at: safeTicket.created_at ?? null,
      updated_at: safeTicket.updated_at ?? null,
      closed_at: safeTicket.closing_date ?? null,
      expected_delivery_at: safeTicket.expected_date ?? null,
    };

    return {
      ticket_overview: overview,
      timeline_updates: this.mapArray(safeTicket.details, detail => this.mapDetail(detail)),
      additional_context: {
        customer_phone: safeTicket.phone ?? null,
        customer_email: safeTicket.email ?? null,
        participant_users: this.mapArray(safeTicket.participants, user => this.mapUser(user)),
        related_users: this.mapArray(safeTicket.ticketUsers, ticketUser =>
          this.mapTicketUser(ticketUser)
        ),
        assigned_tags: this.mapArray(safeTicket.ticket_tags, tag => this.mapTag(tag)),
        attached_documents: this.mapArray(safeTicket.documents, document =>
          this.mapDocument(document)
        ),
        related_tasks: this.mapArray(safeTicket.tasks, task => this.mapTask(task)),
        linked_sprint: this.mapEntity(safeTicket.sprint, 'sprint'),
        process_phase: this.mapEntity(safeTicket.phase, 'phase'),
        linked_project: this.mapEntity(safeTicket.project, 'project'),
        linked_issue: this.mapEntity(safeTicket.issue, 'issue'),
        service_department: this.mapEntity(safeTicket.department, 'department'),
        internal_notes: safeTicket.notes ?? null,
      },
      metadata: {
        last_update_timestamp: this.resolveLastUpdate(ticket),
      },
    };
  }

  private mapArray<T>(source: T[] | undefined, mapper: (item: T) => any): any[] {
    if (!Array.isArray(source)) {
      return [];
    }

    return source.map(item => mapper(item)).filter(item => item !== null && item !== undefined);
  }

  private mapUser(user: any) {
    if (!user) {
      return null;
    }

    return {
      user_id: user.id ?? null,
      full_name: user.name ?? null,
      username: user.login ?? null,
      email: user.email ?? null,
      phone: user.phone ?? null,
      profile_type: user.profile ?? user.type ?? null,
    };
  }

  private mapEntity(entity: any, keyPrefix: string = '') {
    if (!entity) {
      return null;
    }

    const prefix = keyPrefix ? `${keyPrefix}_` : '';

    return {
      id: entity.id ?? entity._id ?? null,
      code: entity.code ?? entity.ref ?? entity.key ?? null,
      label: entity.description ?? entity.name ?? entity.title ?? entity.label ?? null,
    } as Record<string, any>;
  }

  private mapDetail(detail: any) {
    if (!detail) {
      return null;
    }

    const visibility = detail.pub ?? detail.public ?? detail.is_public ?? detail.isPublic;

    return {
      id: detail.id ?? null,
      is_public: visibility === undefined ? null : Boolean(visibility),
      action_summary: detail.operation ?? null,
      note_text: detail.observation ?? null,
      created_at: detail.created_at ?? null,
      author: this.mapUser(detail.user),
    };
  }

  private mapTicketUser(ticketUser: any) {
    if (!ticketUser) {
      return null;
    }

    return {
      role: ticketUser.user_type ?? ticketUser.type ?? null,
      collaborator: this.mapUser(ticketUser.user ?? ticketUser),
    };
  }

  private mapTag(tag: any) {
    if (!tag) {
      return null;
    }

    const tagData = tag.tag ?? tag;

    return {
      id: tagData.id ?? tag.tag_id ?? null,
      label: tagData.name ?? tagData.description ?? null,
      color: tagData.color ?? null,
    };
  }

  private mapDocument(document: any) {
    if (!document) {
      return null;
    }

    return {
      id: document.id ?? null,
      name: document.name ?? document.original_name ?? null,
      link: document.link_file ?? document.url ?? document.link ?? null,
      uploaded_at: document.created_at ?? null,
    };
  }

  private mapTask(task: any) {
    if (!task) {
      return null;
    }

    const statusEntity =
      typeof task.status === 'object' && task.status !== null ? task.status : null;
    const statusLabel = statusEntity
      ? (statusEntity.description ?? statusEntity.name ?? statusEntity.label ?? null)
      : typeof task.status === 'string'
        ? task.status
        : null;

    return {
      id: task.id ?? null,
      summary: task.description ?? task.name ?? null,
      status: statusLabel,
      expected_completion: task.expected_date ?? null,
      is_finished: task.finished ?? false,
      estimated_effort_hours: task.estimated_effort ?? null,
      sequence_position: task.sequence ?? null,
    };
  }

  private resolveLastUpdate(ticket: Ticket): string | null {
    const safeTicket: any = ticket;
    const details = Array.isArray(safeTicket.details) ? [...(safeTicket.details as any[])] : [];

    const mostRecentDetail = details
      .filter(detail => detail?.created_at)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0];

    const updatedAt = safeTicket.updated_at;
    const closingDate = safeTicket.closing_date;
    const createdAt = safeTicket.created_at;

    return mostRecentDetail?.created_at ?? updatedAt ?? closingDate ?? createdAt ?? null;
  }

  private getCustomerCode(ticket: Ticket): string | null {
    const customer: any = ticket.customer;
    if (!customer) {
      return null;
    }

    return customer.code ?? customer.ref ?? null;
  }

  private async writeToClipboard(text: string): Promise<boolean> {
    if (!text) {
      return false;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        console.warn('navigator.clipboard.writeText failed', error);
      }
    }

    if (typeof document !== 'undefined') {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '-1000px';
        textarea.style.left = '-1000px';
        document.body.appendChild(textarea);
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        return successful;
      } catch (error) {
        console.error('document.execCommand copy failed', error);
      }
    }

    return false;
  }
}
